import "./style.css";
import { AudioDirector } from "./audio";
import { MAX_WEAPON_LEVEL, STAGES } from "./config";
import { ThunderGame, type GameResult, type HudSnapshot } from "./game";

interface CampaignSave {
  unlocked: number;
  cleared: boolean[];
  highScores: number[];
  muted: boolean;
}

interface DebugApi {
  startStage(stage: number): void;
  completeStage(): void;
  unlockAll(): void;
  getState(): { selectedStage: number; unlocked: number };
}

declare global {
  interface Window {
    __THUNDER_REALM__?: DebugApi;
  }
}

const SAVE_KEY = "thunder-realm-eight.save.v1";
const query = new URLSearchParams(window.location.search);

function element<T extends HTMLElement>(selector: string): T {
  const found = document.querySelector<T>(selector);
  if (!found) throw new Error(`Missing required element: ${selector}`);
  return found;
}

function defaultSave(): CampaignSave {
  return {
    unlocked: 1,
    cleared: Array.from({ length: STAGES.length }, () => false),
    highScores: Array.from({ length: STAGES.length }, () => 0),
    muted: false,
  };
}

function loadSave(): CampaignSave {
  try {
    const raw = window.localStorage.getItem(SAVE_KEY);
    if (!raw) return defaultSave();
    const parsed = JSON.parse(raw) as Partial<CampaignSave>;
    return {
      unlocked: Math.max(1, Math.min(STAGES.length, Number(parsed.unlocked) || 1)),
      cleared: STAGES.map((_, index) => Boolean(parsed.cleared?.[index])),
      highScores: STAGES.map((_, index) => Math.max(0, Number(parsed.highScores?.[index]) || 0)),
      muted: Boolean(parsed.muted),
    };
  } catch {
    return defaultSave();
  }
}

function persistSave(save: CampaignSave): void {
  try {
    window.localStorage.setItem(SAVE_KEY, JSON.stringify(save));
  } catch {
    // The game still works when storage is unavailable.
  }
}

const canvas = element<HTMLCanvasElement>("#game-canvas");
const menuScreen = element<HTMLElement>("#menu-screen");
const briefingScreen = element<HTMLElement>("#briefing-screen");
const pauseScreen = element<HTMLElement>("#pause-screen");
const resultScreen = element<HTMLElement>("#result-screen");
const combatHud = element<HTMLElement>("#combat-hud");
const bossHud = element<HTMLElement>("#boss-hud");
const stageProgress = element<HTMLElement>("#stage-progress");
const mobileBomb = element<HTMLButtonElement>("#mobile-bomb");
const pauseButton = element<HTMLButtonElement>("#pause-button");
const stageGrid = element<HTMLElement>("#stage-grid");
const launchButton = element<HTMLButtonElement>("#launch-button");
const briefingLaunch = element<HTMLButtonElement>("#briefing-launch");
const briefingBack = element<HTMLButtonElement>("#briefing-back");
const resumeButton = element<HTMLButtonElement>("#resume-button");
const restartButton = element<HTMLButtonElement>("#restart-button");
const quitButton = element<HTMLButtonElement>("#quit-button");
const resultPrimary = element<HTMLButtonElement>("#result-primary");
const resultRetry = element<HTMLButtonElement>("#result-retry");
const resultMenu = element<HTMLButtonElement>("#result-menu");
const soundButton = element<HTMLButtonElement>("#sound-button");
const fullscreenButton = element<HTMLButtonElement>("#fullscreen-button");
const damageFlash = element<HTMLElement>("#damage-flash");
const toastStack = element<HTMLElement>("#toast-stack");
const hudStageIndex = element<HTMLElement>("#hud-stage-index");
const hudStageName = element<HTMLElement>("#hud-stage-name");
const hudHp = element<HTMLElement>("#hud-hp");
const hudHpMax = element<HTMLElement>("#hud-hp-max");
const hudHpBar = element<HTMLElement>("#hud-hp-bar");
const hudScore = element<HTMLElement>("#hud-score");
const hudCombo = element<HTMLElement>("#hud-combo");
const hudWeapon = element<HTMLElement>("#hud-weapon");
const hudWeaponBar = element<HTMLElement>("#hud-weapon-bar");
const hudBombs = element<HTMLElement>("#hud-bombs");
const mobileBombCount = element<HTMLElement>("#mobile-bomb-count");
const stageProgressBar = element<HTMLElement>("#stage-progress-bar");
const bossName = element<HTMLElement>("#boss-name");
const bossHpBar = element<HTMLElement>("#boss-hp-bar");

let campaignSave = loadSave();
const unlockAllFromQuery = query.get("unlock") === "all";
if (unlockAllFromQuery) campaignSave.unlocked = STAGES.length;
let selectedStage = Math.max(0, campaignSave.unlocked - 1);
let lastResultWasVictory = false;

const audio = new AudioDirector();
audio.setMuted(campaignSave.muted);

const game = new ThunderGame(canvas, audio, {
  onHud: updateHud,
  onBoss: updateBoss,
  onToast: showToast,
  onPlayerHit: flashDamage,
  onBossWarning: () => {
    document.querySelector(".game-stage")?.classList.add("is-boss-warning");
    window.setTimeout(() => document.querySelector(".game-stage")?.classList.remove("is-boss-warning"), 1400);
  },
  onComplete: (result) => showResult(result, true),
  onGameOver: (result) => showResult(result, false),
});

function renderStageGrid(): void {
  stageGrid.replaceChildren();
  STAGES.forEach((stage, index) => {
    const button = document.createElement("button");
    const locked = index >= campaignSave.unlocked;
    button.type = "button";
    button.className = "stage-node";
    if (index === selectedStage) button.classList.add("is-selected");
    if (campaignSave.cleared[index]) button.classList.add("is-cleared");
    button.disabled = locked;
    button.title = locked
      ? `完成第 ${index} 域后解锁「${stage.name}」`
      : `${stage.name}${campaignSave.highScores[index] ? ` · 最高分 ${campaignSave.highScores[index]}` : ""}`;
    button.setAttribute("aria-label", button.title);
    button.innerHTML = `<span>${stage.roman}</span><small>${locked ? "LOCKED" : stage.subtitle.split(" ")[0]}</small>`;
    button.addEventListener("click", () => {
      selectedStage = index;
      renderStageGrid();
      updateCampaignProgress();
    });
    stageGrid.append(button);
  });
  updateCampaignProgress();
}

function updateCampaignProgress(): void {
  element<HTMLElement>("#campaign-progress").textContent =
    `${String(selectedStage + 1).padStart(2, "0")} / ${String(STAGES.length).padStart(2, "0")}`;
  const stage = STAGES[selectedStage];
  const launchLabel = launchButton.querySelector("b");
  if (launchLabel) launchLabel.textContent = `进入「${stage.name}」`;
}

function openBriefing(stageIndex = selectedStage): void {
  selectedStage = Math.max(0, Math.min(STAGES.length - 1, stageIndex));
  const stage = STAGES[selectedStage];
  element<HTMLElement>("#briefing-index").textContent = String(stage.id).padStart(2, "0");
  element<HTMLElement>("#briefing-eyebrow").textContent = stage.domain;
  element<HTMLElement>("#briefing-title").textContent = stage.name;
  element<HTMLElement>("#briefing-description").textContent = stage.description;
  element<HTMLElement>("#briefing-hazard").textContent = stage.hazard;
  element<HTMLElement>("#briefing-boss").textContent = stage.boss;
  menuScreen.classList.add("is-hidden");
  resultScreen.classList.add("is-hidden");
  briefingScreen.classList.remove("is-hidden");
}

async function beginStage(): Promise<void> {
  await audio.unlock();
  briefingScreen.classList.add("is-hidden");
  menuScreen.classList.add("is-hidden");
  resultScreen.classList.add("is-hidden");
  pauseScreen.classList.add("is-hidden");
  combatHud.classList.remove("is-hidden");
  stageProgress.classList.remove("is-hidden");
  pauseButton.classList.remove("is-hidden");
  if (window.matchMedia("(pointer: coarse)").matches || window.innerWidth <= 720) {
    mobileBomb.classList.remove("is-hidden");
  }
  game.start(selectedStage);
  canvas.focus({ preventScroll: true });
}

function showMenu(): void {
  game.returnToIdle();
  selectedStage = Math.min(selectedStage, campaignSave.unlocked - 1);
  renderStageGrid();
  menuScreen.classList.remove("is-hidden");
  briefingScreen.classList.add("is-hidden");
  pauseScreen.classList.add("is-hidden");
  resultScreen.classList.add("is-hidden");
  combatHud.classList.add("is-hidden");
  bossHud.classList.add("is-hidden");
  stageProgress.classList.add("is-hidden");
  mobileBomb.classList.add("is-hidden");
  pauseButton.classList.add("is-hidden");
}

function pauseGame(): void {
  if (!game.isPlaying) return;
  game.pause();
  pauseScreen.classList.remove("is-hidden");
}

function resumeGame(): void {
  game.resume();
  pauseScreen.classList.add("is-hidden");
  canvas.focus({ preventScroll: true });
}

function restartCurrentStage(): void {
  pauseScreen.classList.add("is-hidden");
  resultScreen.classList.add("is-hidden");
  void beginStage();
}

function updateHud(snapshot: HudSnapshot): void {
  hudStageIndex.textContent = `域 ${String(snapshot.stageIndex + 1).padStart(2, "0")}`;
  hudStageName.textContent = snapshot.stageName;
  hudHp.textContent = String(Math.ceil(snapshot.hp));
  hudHpMax.textContent = String(snapshot.maxHp);
  hudHpBar.style.transform = `scaleX(${Math.max(0, snapshot.hp / snapshot.maxHp)})`;
  hudScore.textContent = String(snapshot.score).padStart(7, "0");
  hudCombo.textContent = `COMBO × ${snapshot.multiplier.toFixed(2)} · ${snapshot.combo}`;
  hudWeapon.textContent = String(snapshot.weaponLevel);
  hudWeaponBar.style.transform = `scaleX(${snapshot.weaponLevel / MAX_WEAPON_LEVEL})`;
  hudBombs.textContent = String(snapshot.bombs);
  mobileBombCount.textContent = `×${snapshot.bombs}`;
  stageProgressBar.style.transform = `scaleX(${snapshot.progress})`;
  mobileBomb.disabled = snapshot.bombs <= 0;
  mobileBomb.style.opacity = snapshot.bombs <= 0 ? "0.35" : "1";
}

function updateBoss(show: boolean, name: string, ratio: number): void {
  bossHud.classList.toggle("is-hidden", !show);
  if (!show) return;
  bossName.textContent = name;
  bossHpBar.style.transform = `scaleX(${Math.max(0, ratio)})`;
}

function showToast(title: string, caption: string): void {
  const toast = document.createElement("div");
  toast.className = "toast";
  toast.innerHTML = `${title}<small>${caption}</small>`;
  toastStack.append(toast);
  window.setTimeout(() => toast.remove(), 2050);
  while (toastStack.children.length > 3) {
    toastStack.firstElementChild?.remove();
  }
}

function flashDamage(): void {
  damageFlash.classList.remove("is-active");
  void damageFlash.offsetWidth;
  damageFlash.classList.add("is-active");
}

function showResult(result: GameResult, victory: boolean): void {
  lastResultWasVictory = victory;
  resultRetry.classList.toggle("is-hidden", !victory);
  combatHud.classList.add("is-hidden");
  bossHud.classList.add("is-hidden");
  stageProgress.classList.add("is-hidden");
  mobileBomb.classList.add("is-hidden");
  pauseButton.classList.add("is-hidden");
  pauseScreen.classList.add("is-hidden");

  if (victory) {
    campaignSave.cleared[result.stageIndex] = true;
    campaignSave.unlocked = Math.max(
      campaignSave.unlocked,
      Math.min(STAGES.length, result.stageIndex + 2),
    );
    campaignSave.highScores[result.stageIndex] = Math.max(
      campaignSave.highScores[result.stageIndex],
      result.score,
    );
    persistSave(campaignSave);
  }

  element<HTMLElement>("#result-rank").textContent = result.rank;
  element<HTMLElement>("#result-kicker").textContent = victory ? "DOMAIN CLEARED" : "SORTIE FAILED";
  element<HTMLElement>("#result-title").textContent = victory ? "空域已贯通" : "战机链路中断";
  element<HTMLElement>("#result-description").textContent = victory
    ? result.stageIndex === STAGES.length - 1
      ? "零号协议已经熄灭。八域导航信标同时重启，天空终于重新属于飞行者。"
      : "雷暴墙正在瓦解，下一空域的导航信标已经点亮。"
    : "本次飞行数据已回传。调整走位，在密集弹幕出现前先清掉高威胁单位。";
  element<HTMLElement>("#result-score").textContent = String(result.score).padStart(7, "0");
  element<HTMLElement>("#result-combo").textContent = `× ${result.maxCombo}`;
  element<HTMLElement>("#result-hp").textContent = `${result.hpPercent}%`;

  const primarySmall = resultPrimary.querySelector("small");
  const primaryLabel = resultPrimary.querySelector("b");
  const primaryIcon = resultPrimary.querySelector("i");
  if (victory && result.stageIndex < STAGES.length - 1) {
    if (primarySmall) primarySmall.textContent = "NEXT DOMAIN";
    if (primaryLabel) primaryLabel.textContent = "进入下一空域";
    if (primaryIcon) primaryIcon.textContent = "↗";
  } else if (victory) {
    if (primarySmall) primarySmall.textContent = "CAMPAIGN COMPLETE";
    if (primaryLabel) primaryLabel.textContent = "返回八域航图";
    if (primaryIcon) primaryIcon.textContent = "◎";
  } else {
    if (primarySmall) primarySmall.textContent = "RETRY";
    if (primaryLabel) primaryLabel.textContent = "重新出击";
    if (primaryIcon) primaryIcon.textContent = "↻";
  }
  resultScreen.classList.remove("is-hidden");
}

function canvasCoordinates(event: PointerEvent): { x: number; y: number } {
  const bounds = canvas.getBoundingClientRect();
  return {
    x: ((event.clientX - bounds.left) / bounds.width) * canvas.width,
    y: ((event.clientY - bounds.top) / bounds.height) * canvas.height,
  };
}

launchButton.addEventListener("click", () => openBriefing());
briefingBack.addEventListener("click", showMenu);
briefingLaunch.addEventListener("click", () => void beginStage());
pauseButton.addEventListener("click", pauseGame);
resumeButton.addEventListener("click", resumeGame);
restartButton.addEventListener("click", restartCurrentStage);
quitButton.addEventListener("click", showMenu);
resultRetry.addEventListener("click", restartCurrentStage);
resultMenu.addEventListener("click", showMenu);
mobileBomb.addEventListener("pointerdown", (event) => {
  event.preventDefault();
  event.stopPropagation();
  game.useBomb();
});

resultPrimary.addEventListener("click", () => {
  if (!lastResultWasVictory) {
    restartCurrentStage();
    return;
  }
  if (selectedStage >= STAGES.length - 1) {
    showMenu();
    return;
  }
  selectedStage += 1;
  openBriefing(selectedStage);
});

soundButton.addEventListener("click", async () => {
  await audio.unlock();
  campaignSave.muted = audio.toggleMuted();
  soundButton.classList.toggle("is-muted", campaignSave.muted);
  soundButton.setAttribute("aria-label", campaignSave.muted ? "开启声音" : "关闭声音");
  persistSave(campaignSave);
});

fullscreenButton.addEventListener("click", async () => {
  try {
    if (!document.fullscreenElement) {
      await element<HTMLElement>("#game-shell").requestFullscreen();
    } else {
      await document.exitFullscreen();
    }
  } catch {
    showToast("浏览器未允许全屏", "FULLSCREEN REQUEST DENIED");
  }
});

canvas.addEventListener("pointerdown", (event) => {
  if (!game.isPlaying) return;
  event.preventDefault();
  canvas.setPointerCapture(event.pointerId);
  const point = canvasCoordinates(event);
  game.setPointer(point.x, point.y, true);
});

canvas.addEventListener("pointermove", (event) => {
  if (!game.isPlaying || !canvas.hasPointerCapture(event.pointerId)) return;
  event.preventDefault();
  const point = canvasCoordinates(event);
  game.setPointer(point.x, point.y, true);
});

canvas.addEventListener("pointerup", (event) => {
  if (canvas.hasPointerCapture(event.pointerId)) {
    canvas.releasePointerCapture(event.pointerId);
  }
  const point = canvasCoordinates(event);
  game.setPointer(point.x, point.y, false);
});

canvas.addEventListener("pointercancel", (event) => {
  if (canvas.hasPointerCapture(event.pointerId)) {
    canvas.releasePointerCapture(event.pointerId);
  }
  game.setPointer(240, 680, false);
});

window.addEventListener("keydown", (event) => {
  if (
    ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Space", "KeyW", "KeyA", "KeyS", "KeyD"].includes(
      event.code,
    )
  ) {
    event.preventDefault();
  }
  if (event.code === "Space" && !event.repeat) {
    game.useBomb();
    return;
  }
  if ((event.code === "Escape" || event.code === "KeyP") && !event.repeat) {
    if (game.isPaused) resumeGame();
    else pauseGame();
    return;
  }
  game.setKey(event.code, true);
});

window.addEventListener("keyup", (event) => game.setKey(event.code, false));

document.addEventListener("visibilitychange", () => {
  if (document.hidden && game.isPlaying) pauseGame();
});

window.addEventListener("beforeunload", () => game.destroy());

soundButton.classList.toggle("is-muted", campaignSave.muted);
soundButton.setAttribute("aria-label", campaignSave.muted ? "开启声音" : "关闭声音");
renderStageGrid();

window.__THUNDER_REALM__ = {
  startStage(stage: number): void {
    selectedStage = Math.max(0, Math.min(STAGES.length - 1, Math.floor(stage) - 1));
    void beginStage();
  },
  completeStage(): void {
    game.debugCompleteStage();
  },
  unlockAll(): void {
    campaignSave.unlocked = STAGES.length;
    persistSave(campaignSave);
    renderStageGrid();
  },
  getState(): { selectedStage: number; unlocked: number } {
    return { selectedStage: selectedStage + 1, unlocked: campaignSave.unlocked };
  },
};

if (query.get("debug") === "1") {
  const panel = document.createElement("div");
  panel.className = "debug-panel";
  panel.innerHTML = `
    <button type="button" data-debug-action="boss">测试：召唤 Boss</button>
    <button type="button" data-debug-action="complete">测试：完成本关</button>
    <button type="button" data-debug-action="unlock">测试：解锁八域</button>
  `;
  panel.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof HTMLButtonElement)) return;
    const action = target.dataset.debugAction;
    if (action === "boss") game.debugSpawnBoss();
    if (action === "complete") game.debugCompleteStage();
    if (action === "unlock") {
      campaignSave.unlocked = STAGES.length;
      persistSave(campaignSave);
      renderStageGrid();
      showToast("八域演练权限已开启", "DEBUG ACCESS");
    }
  });
  element<HTMLElement>(".game-stage").append(panel);
}
